import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL;

const REPORTS = [
  { id: 1, name: 'Universe Top 2', desc: 'Top 2 funds from each category', endpoint: '/api/retail/report/universe-top2', params: {} },
  { id: 2, name: 'Category Best', desc: 'Best funds by category', endpoint: '/api/retail/report/category-best', params: {} },
  { id: 3, name: 'My Watchlist', desc: 'Your saved watchlist funds', endpoint: '/api/retail/report/watchlist', params: {} },
  { id: 4, name: 'SIP Plan', desc: 'Personalized SIP recommendations', endpoint: '/api/retail/report/sip', params: { risk_profile: 'Moderate', sip_amount: 5000 } },
  { id: 5, name: 'Portfolio Check', desc: 'Analyse your current portfolio', endpoint: '/api/retail/report/portfolio-check', params: {} },
  { id: 6, name: 'Goal Planner', desc: 'Plan investments for your goals', endpoint: '/api/retail/report/goal-planner', params: {} },
  { id: 7, name: 'Fund Compare', desc: 'Compare two or more funds', endpoint: '/api/retail/report/compare', params: {} },
];

export default function RetailDashboard() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [sipAmount, setSipAmount] = useState(5000);
  const [riskProfile, setRiskProfile] = useState('Moderate');

  const headers = { Authorization: `Bearer ${token}` };

  const downloadReport = async (report, format) => {
    setLoading(`${report.id}-${format}`);
    setMsg('');
    try {
      let params = { ...report.params, format };
      if (report.id === 4) params = { ...params, risk_profile: riskProfile, sip_amount: sipAmount };

      const res = await axios.get(`${API}${report.endpoint}`, {
        headers, params, responseType: 'blob'
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.name.replace(/ /g,'_')}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setMsg(`✅ ${report.name} downloaded!`);
    } catch {
      setMsg(`❌ Failed to download ${report.name}`);
    } finally {
      setLoading(null);
    }
  };

  const handleSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setSearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/api/retail/search`, { headers, params: { q } });
      setSearchResults(res.data.funds || res.data || []);
    } catch { setSearchResults([]); }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={s.page}>
      <div style={s.navbar}>
        <span style={s.brand}>🏦 Fund Intelligence Platform</span>
        <div style={s.navRight}>
          <span style={s.navUser}>👤 {user?.name || user?.email}</span>
          <button onClick={handleLogout} style={s.logoutBtn}>Logout</button>
        </div>
      </div>

      <div style={s.container}>
        <h2 style={s.heading}>Investor Dashboard</h2>
        {msg && <div style={s.msgBox}>{msg}</div>}

        {/* Search */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>🔍 Fund Search</h3>
          <input
            placeholder="Search funds (e.g. axis, sbi...)"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            style={s.input}
          />
          {searchResults.length > 0 && (
            <div style={s.searchResults}>
              {searchResults.slice(0, 8).map((f, i) => (
                <div key={i} style={s.searchItem}>{f.name || f.scheme_name || f}</div>
              ))}
            </div>
          )}
        </div>

        {/* Filters */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>⚙️ SIP Parameters</h3>
          <div style={s.filters}>
            <div style={s.filterGroup}>
              <label style={s.label}>Risk Profile</label>
              <select value={riskProfile} onChange={e => setRiskProfile(e.target.value)} style={s.select}>
                {['Conservative', 'Moderate', 'Aggressive'].map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div style={s.filterGroup}>
              <label style={s.label}>Monthly SIP (₹)</label>
              <input type="number" value={sipAmount}
                onChange={e => setSipAmount(e.target.value)} style={s.select} />
            </div>
          </div>
        </div>

        {/* Reports */}
        <div style={s.card}>
          <h3 style={s.cardTitle}>📊 My Reports (7 Reports)</h3>
          <div style={s.grid}>
            {REPORTS.map(r => (
              <div key={r.id} style={s.reportCard}>
                <div style={s.reportNum}>Report {r.id}</div>
                <div style={s.reportName}>{r.name}</div>
                <div style={s.reportDesc}>{r.desc}</div>
                <div style={s.btnRow}>
                  <button
                    onClick={() => downloadReport(r, 'pdf')}
                    disabled={loading === `${r.id}-pdf`}
                    style={s.pdfBtn}>
                    {loading === `${r.id}-pdf` ? '⏳' : '📄'} PDF
                  </button>
                  <button
                    onClick={() => downloadReport(r, 'excel')}
                    disabled={loading === `${r.id}-excel`}
                    style={s.xlsBtn}>
                    {loading === `${r.id}-excel` ? '⏳' : '📊'} Excel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f5f7fa', fontFamily: "'Segoe UI', sans-serif" },
  navbar: { background: '#1565C0', color: '#fff', padding: '14px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  brand: { fontWeight: '700', fontSize: '18px' },
  navRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  navUser: { fontSize: '14px' },
  logoutBtn: { background: '#CC0000', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: '600' },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' },
  heading: { color: '#1565C0', marginBottom: '16px' },
  msgBox: { background: '#E3F0FF', border: '1px solid #1565C0', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', color: '#1565C0' },
  card: { background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', marginBottom: '20px' },
  cardTitle: { color: '#1565C0', marginBottom: '16px', marginTop: 0 },
  input: { width: '100%', padding: '12px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' },
  searchResults: { marginTop: '8px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' },
  searchItem: { padding: '10px 14px', borderBottom: '1px solid #f0f0f0', fontSize: '13px' },
  filters: { display: 'flex', gap: '20px', flexWrap: 'wrap' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '180px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#333' },
  select: { padding: '10px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '14px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  reportCard: { border: '1.5px solid #E3F0FF', borderRadius: '10px', padding: '18px', background: '#fafcff' },
  reportNum: { fontSize: '11px', color: '#1565C0', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' },
  reportName: { fontSize: '15px', fontWeight: '700', color: '#222', marginBottom: '6px' },
  reportDesc: { fontSize: '13px', color: '#666', marginBottom: '14px' },
  btnRow: { display: 'flex', gap: '8px' },
  pdfBtn: { background: '#CC0000', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  xlsBtn: { background: '#2e7d32', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
};
